import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";

import logoUrl from "src/assets/logo/logo.png";
import fontRUrl from "src/assets/fonts/Roboto-Regular.ttf";
import fontBUrl from "src/assets/fonts/Roboto-Bold.ttf";

// ─── Constantes de diseño ────────────────────────────────────────────────────

const PW = 595;
const PH = 842;
const ML = 50;
const MR = 545;
const CW = MR - ML;
const CONTENT_BOTTOM = 92;

const C = {
  naranja: rgb(1, 0.502, 0.18),
  verde: rgb(0.18, 0.49, 0.196),
  textoOscuro: rgb(0.2, 0.2, 0.2),
  textoGris: rgb(0.4, 0.4, 0.4),
  textoClaro: rgb(0.55, 0.55, 0.55),
  blanco: rgb(1, 1, 1),
  bgTabla: rgb(0.965, 0.965, 0.965),
  bgAuth: rgb(1, 0.976, 0.902),
  bordeAuth: rgb(1, 0.878, 0.51),
  lineaSep: rgb(0.878, 0.878, 0.878),
  verdeClaro: rgb(0.8, 0.95, 0.8),
};

// Medio de pago: solo débito / crédito / prepago
const MEDIO_PAGO = {
  VD: "Débito",
  VN: "Crédito",
  VC: "Crédito",
  SI: "Crédito",
  S2: "Crédito",
  NC: "Crédito",
  VP: "Prepago",
};

// Modalidad de cuotas: derivada del código + installments_number
function describirCuotas(payment_type_code, installments_number) {
  switch (payment_type_code) {
    case "VD":
    case "VN":
    case "VP":
      return "Sin cuotas";
    case "VC":
      return `${installments_number} cuotas`;
    case "SI":
      return "3 cuotas sin interés";
    case "S2":
      return "2 cuotas sin interés";
    case "NC":
      return `${installments_number} cuotas sin interés`;
    default:
      return installments_number > 0
        ? `${installments_number} cuotas`
        : "Sin cuotas";
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatearMonto = (monto) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }).format(
    monto,
  );

const formatearFecha = (isoDate) => {
  if (!isoDate) return "—";
  return new Date(isoDate).toLocaleString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatearIdentidad = ({ nombre, rut } = {}) =>
  [nombre, rut ? `RUT ${rut}` : null].filter(Boolean).join(" · ");

const dividirTextoPorAncho = ({ texto, font, size, maxWidth }) => {
  const contenido = String(texto ?? "").trim();
  if (!contenido) return [];

  const lineas = [];
  let lineaActual = "";

  for (const palabra of contenido.split(/\s+/)) {
    const candidata = lineaActual ? `${lineaActual} ${palabra}` : palabra;
    if (font.widthOfTextAtSize(candidata, size) <= maxWidth) {
      lineaActual = candidata;
      continue;
    }

    if (lineaActual) {
      lineas.push(lineaActual);
      lineaActual = "";
    }

    if (font.widthOfTextAtSize(palabra, size) <= maxWidth) {
      lineaActual = palabra;
      continue;
    }

    let fragmento = "";
    for (const caracter of palabra) {
      const fragmentoCandidato = `${fragmento}${caracter}`;
      if (
        fragmento &&
        font.widthOfTextAtSize(fragmentoCandidato, size) > maxWidth
      ) {
        lineas.push(fragmento);
        fragmento = caracter;
      } else {
        fragmento = fragmentoCandidato;
      }
    }
    lineaActual = fragmento;
  }

  if (lineaActual) lineas.push(lineaActual);
  return lineas;
};

/** Obtiene los bytes de una URL via fetch y los retorna como ArrayBuffer */
const fetchBytes = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`No se pudo cargar el asset: ${url}`);
  return res.arrayBuffer();
};

/**
 * Detecta el formato de imagen por magic bytes y la embebe en el documento.
 * Soporta PNG (89 50 4E 47) y JPEG (FF D8 FF).
 */
const embedLogo = async (doc, bytes) => {
  const view = new Uint8Array(bytes);
  const isPng = view[0] === 0x89 && view[1] === 0x50;
  return isPng ? doc.embedPng(bytes) : doc.embedJpg(bytes);
};

// ─── Función principal ─────────────────────────────────────────────────────----

/**
 * Genera el comprobante de pago WebPay como PDF y lo descarga en el browser.
 *
 * Construye el PDF con pdf-lib usando fuentes Roboto embebidas (subset),
 * y activa la descarga del archivo directamente en el navegador del usuario.
 *
 * @param {object} params
 * @param {string}        params.nombreCliente  - Nombre completo del cliente
 * @param {string|number} params.folio          - Folio de la factura
 * @param {Array<{folio: string|number, tipoFactura: string, monto: number, nombreSociedad?: string, rutSociedad?: string}>} [params.facturas] - En caso de pago múltiple, array con detalle de cada factura
 * @param {string}        params.tipoPago       - "PLAN" o "TAREA", para describir el servicio pagado
 * @param {string}        [params.descripcion]  - Descripción adicional del servicio (opcional, se muestra solo en modo individual)
 * @param {string}        [params.nombreSociedad] - Sociedad dueña de la factura individual
 * @param {string}        [params.rutSociedad] - RUT de la sociedad dueña de la factura individual
 * @param {{nombre?: string, rut?: string}} [params.cuentaPagadora] - Cuenta que inició el pago
 * @param {number}        params.monto          - Monto pagado en pesos chilenos
 * @param {object}        params.datosTransbank - Objeto con token_ws y commitResponse
 * @returns {Promise<void>}
 */
export async function generarYDescargarComprobantePDF({
  nombreCliente,
  folio,
  facturas = null,
  tipoPago = null,
  descripcion = null,
  nombreSociedad = null,
  rutSociedad = null,
  cuentaPagadora = null,
  monto,
  datosTransbank,
}) {
  const {
    authorization_code = "—",
    payment_type_code = "—",
    installments_number = 0,
    transaction_date = null,
    card_detail = {},
  } = datosTransbank?.commitResponse ?? {};

  const tipoTarjeta = MEDIO_PAGO[payment_type_code] ?? payment_type_code;
  const ultimos4 = card_detail?.card_number ?? "****";
  const cuotas = describirCuotas(payment_type_code, installments_number);
  const esMultiple = Array.isArray(facturas) && facturas.length > 0;
  const folioStr = esMultiple ? `${facturas.length} facturas` : String(folio);
  // ---- Cargar assets en paralelo ----
  const [logoBytes, fontRBytes, fontBBytes] = await Promise.all([
    fetchBytes(logoUrl),
    fetchBytes(fontRUrl),
    fetchBytes(fontBUrl),
  ]);

  // ---- Crear documento ----
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);

  const fR = await doc.embedFont(fontRBytes, { subset: true }); // Roboto Regular
  const fB = await doc.embedFont(fontBBytes, { subset: true }); // Roboto Bold

  let page = doc.addPage([PW, PH]);
  let y = PH - 45;

  const dibujarBarraSuperior = (pagina) => {
    pagina.drawRectangle({
      x: 0,
      y: PH - 8,
      width: PW,
      height: 8,
      color: C.naranja,
    });
  };

  const dibujarEncabezadoContinuacion = ({
    tituloSeccion,
    incluirColumnasFacturas = false,
  }) => {
    page = doc.addPage([PW, PH]);
    dibujarBarraSuperior(page);

    page.drawText("COMPROBANTE DE PAGO - CONTINUACIÓN", {
      x: ML,
      y: PH - 42,
      size: 12,
      font: fB,
      color: C.naranja,
    });
    page.drawText("Transbank WebPay Plus", {
      x: ML,
      y: PH - 57,
      size: 8,
      font: fR,
      color: C.textoClaro,
    });
    page.drawLine({
      start: { x: ML, y: PH - 72 },
      end: { x: MR, y: PH - 72 },
      thickness: 1.5,
      color: C.naranja,
    });

    const sectionTop = PH - 92;
    const sectionBottom = sectionTop - 24;
    page.drawRectangle({
      x: ML,
      y: sectionBottom,
      width: CW,
      height: 24,
      color: C.textoOscuro,
    });
    page.drawText(tituloSeccion, {
      x: ML + 12,
      y: sectionBottom + 12 - 9 * 0.35,
      size: 9,
      font: fB,
      color: C.blanco,
    });

    if (!incluirColumnasFacturas) return sectionBottom - 16;

    const subBottom = sectionBottom - 22;
    page.drawRectangle({
      x: ML,
      y: subBottom,
      width: CW,
      height: 22,
      color: C.bgTabla,
    });
    const colFolio = ML + 12;
    const colTipo = ML + CW * 0.4;
    const colMontoR = MR - 12;
    [
      { label: "Folio", x: colFolio },
      { label: "Tipo / Sociedad titular", x: colTipo },
      { label: "Monto", x: colMontoR - fB.widthOfTextAtSize("Monto", 9) },
    ].forEach(({ label, x }) => {
      page.drawText(label, {
        x,
        y: subBottom + 11 - 9 * 0.35,
        size: 9,
        font: fB,
        color: C.textoGris,
      });
    });
    return subBottom;
  };

  const dibujarFooter = (pagina, numeroPagina, totalPaginas) => {
    pagina.drawLine({
      start: { x: ML, y: 70 },
      end: { x: MR, y: 70 },
      thickness: 0.5,
      color: C.lineaSep,
    });
    pagina.drawText("Taxtic — Asesoría Tributaria Integral", {
      x: ML,
      y: 54,
      size: 9,
      font: fB,
      color: C.textoGris,
    });
    pagina.drawText("www.taxtic.com  |  clientes@taxtic.com", {
      x: ML,
      y: 40,
      size: 8,
      font: fR,
      color: C.textoClaro,
    });
    pagina.drawText("Este documento fue generado automáticamente.", {
      x: ML,
      y: 26,
      size: 8,
      font: fR,
      color: C.textoClaro,
    });
    const paginacion = `Página ${numeroPagina} de ${totalPaginas}`;
    pagina.drawText(paginacion, {
      x: MR - fR.widthOfTextAtSize(paginacion, 8),
      y: 40,
      size: 8,
      font: fR,
      color: C.textoClaro,
    });
  };

  // ---- Barra naranja superior ----
  dibujarBarraSuperior(page);

  // ---- Logo ----
  const LOGO_H = 48;
  try {
    const img = await embedLogo(doc, logoBytes);
    const dims = img.scaleToFit(160, LOGO_H);
    page.drawImage(img, {
      x: ML,
      y: y - dims.height + 10,
      width: dims.width,
      height: dims.height,
    });
  } catch {
    // Si falla el logo, el PDF sigue siendo válido
  }

  // ---- Título alineado a la derecha ----
  const titulo = "COMPROBANTE DE PAGO";
  const subtitulo = "Transbank WebPay Plus";
  page.drawText(titulo, {
    x: MR - fB.widthOfTextAtSize(titulo, 14),
    y,
    size: 14,
    font: fB,
    color: C.naranja,
  });
  page.drawText(subtitulo, {
    x: MR - fR.widthOfTextAtSize(subtitulo, 9),
    y: y - 16,
    size: 9,
    font: fR,
    color: C.textoClaro,
  });

  y -= 58;

  // ---- Separador naranja ----
  page.drawLine({
    start: { x: ML, y },
    end: { x: MR, y },
    thickness: 1.5,
    color: C.naranja,
  });
  y -= 24;

  // ---- Saludo ----
  page.drawText(`Estimado/a ${nombreCliente},`, {
    x: ML,
    y,
    size: 11,
    font: fR,
    color: C.textoOscuro,
  });
  y -= 16;
  page.drawText(
    esMultiple
      ? "Tu pago múltiple ha sido procesado exitosamente. A continuación el detalle:"
      : "Tu pago ha sido procesado exitosamente. A continuación el detalle de la transacción:",
    {
      x: ML,
      y,
      size: 10,
      font: fR,
      color: C.textoGris,
    },
  );
  y -= 28;

  // ---- Bloque verde: monto ----
  const BOX_H = 64;
  const boxBottom = y - BOX_H;

  page.drawRectangle({
    x: ML,
    y: boxBottom,
    width: CW,
    height: BOX_H,
    color: C.verde,
    borderRadius: 4,
  });

  page.drawText("Pago Exitoso", {
    x: ML + 16,
    y: boxBottom + BOX_H - 10 - 10,
    size: 10,
    font: fB,
    color: C.blanco,
  });
  page.drawText("Monto pagado", {
    x: ML + 16,
    y: boxBottom + 10,
    size: 9,
    font: fR,
    color: C.verdeClaro,
  });

  const montoTxt = formatearMonto(monto);
  const montoSize = 24;
  page.drawText(montoTxt, {
    x: MR - fB.widthOfTextAtSize(montoTxt, montoSize) - 16,
    y: boxBottom + BOX_H / 2 - montoSize * 0.35,
    size: montoSize,
    font: fB,
    color: C.blanco,
  });

  y = boxBottom - 24;

  // ---- Tabla de detalles ----
  const HEADER_H = 24;
  const headerBottom = y - HEADER_H;

  page.drawRectangle({
    x: ML,
    y: headerBottom,
    width: CW,
    height: HEADER_H,
    color: C.textoOscuro,
  });
  page.drawText("DETALLE DE LA TRANSACCIÓN", {
    x: ML + 12,
    y: headerBottom + HEADER_H / 2 - 9 * 0.35,
    size: 9,
    font: fB,
    color: C.blanco,
  });

  y = headerBottom;

  const ROW_H = 30;
  const TEXT_PAD = ROW_H / 2 - 10 * 0.35;
  const COL2 = ML + CW * 0.42;

  if (esMultiple) {
    // ---- Sub-encabezado columnas: Folio | Tipo | Monto ----
    const COL_FOLIO = ML + 12;
    const COL_TIPO = ML + CW * 0.4;
    const COL_MONTO_R = MR - 12;

    const SUB_H = 22;
    const subBottom = y - SUB_H;
    page.drawRectangle({
      x: ML,
      y: subBottom,
      width: CW,
      height: SUB_H,
      color: C.bgTabla,
    });

    [
      { label: "Folio", x: COL_FOLIO },
      { label: "Tipo / Sociedad titular", x: COL_TIPO },
      { label: "Monto", x: COL_MONTO_R - fB.widthOfTextAtSize("Monto", 9) },
    ].forEach(({ label, x }) => {
      page.drawText(label, {
        x,
        y: subBottom + SUB_H / 2 - 9 * 0.35,
        size: 9,
        font: fB,
        color: C.textoGris,
      });
    });
    y = subBottom;

    const filasFacturas = facturas.map((f, idx) => {
      const fStr = String(f.folio);
      const fTipo = f.tipoFactura === "PLAN" ? "Plan" : "Tarea";
      const sociedad = formatearIdentidad({
        nombre: f.nombreSociedad,
        rut: f.rutSociedad,
      });
      const lineasSociedad = dividirTextoPorAncho({
        texto: sociedad,
        font: fR,
        size: 8,
        maxWidth: CW - 24,
      });
      const fMonto = formatearMonto(f.monto);
      const rowHeight = ROW_H + lineasSociedad.length * 10;
      return { idx, fStr, fTipo, lineasSociedad, fMonto, rowHeight };
    });

    // ---- Fila por factura ----
    for (const fila of filasFacturas) {
      const { idx, fStr, fTipo, lineasSociedad, fMonto, rowHeight } = fila;

      if (y - rowHeight < CONTENT_BOTTOM) {
        y = dibujarEncabezadoContinuacion({
          tituloSeccion: "DETALLE DE FACTURAS (CONTINUACIÓN)",
          incluirColumnasFacturas: true,
        });
      }

      const rowBottom = y - rowHeight;

      if (idx % 2 === 1) {
        page.drawRectangle({
          x: ML,
          y: rowBottom,
          width: CW,
          height: rowHeight,
          color: C.bgTabla,
        });
      }

      const tY = y - ROW_H + TEXT_PAD;
      page.drawText(fStr, {
        x: COL_FOLIO,
        y: tY,
        size: 10,
        font: fR,
        color: C.textoOscuro,
      });
      page.drawText(fTipo, {
        x: COL_TIPO,
        y: tY,
        size: 10,
        font: fR,
        color: C.textoOscuro,
      });
      for (const [lineaIndex, linea] of lineasSociedad.entries()) {
        page.drawText(linea, {
          x: ML + 12,
          y: tY - 12 - lineaIndex * 10,
          size: 8,
          font: fR,
          color: C.textoClaro,
        });
      }
      page.drawText(fMonto, {
        x: COL_MONTO_R - fR.widthOfTextAtSize(fMonto, 10),
        y: tY,
        size: 10,
        font: fB,
        color: C.textoOscuro,
      });
      page.drawLine({
        start: { x: ML, y: rowBottom },
        end: { x: MR, y: rowBottom },
        thickness: 0.5,
        color: C.lineaSep,
      });
      y = rowBottom;
    }

    const filasTransaccion = [
      ...(formatearIdentidad(cuentaPagadora)
        ? [["Cuenta que realizó el pago", formatearIdentidad(cuentaPagadora)]]
        : []),
      ["Fecha de Pago", formatearFecha(transaction_date)],
      ["Tipo de Tarjeta", tipoTarjeta],
      ["Últimos 4 dígitos", `**** ${ultimos4}`],
      ["Cuotas", cuotas],
    ].map(([label, value]) => {
      const esIdentidad = label === "Cuenta que realizó el pago";
      const lineasValor = esIdentidad
        ? dividirTextoPorAncho({
            texto: value,
            font: fR,
            size: 10,
            maxWidth: CW - 24,
          })
        : [];
      return {
        label,
        value,
        esIdentidad,
        lineasValor,
        rowHeight: ROW_H + lineasValor.length * 12,
      };
    });

    const alturaResumen =
      ROW_H +
      16 +
      filasTransaccion.reduce((total, fila) => total + fila.rowHeight, 0) +
      24 +
      76 +
      20 +
      28;
    if (y - alturaResumen < CONTENT_BOTTOM) {
      y = dibujarEncabezadoContinuacion({
        tituloSeccion: "RESUMEN DE LA TRANSACCIÓN",
      });
    }

    // ---- Fila TOTAL ----
    const totalStr = formatearMonto(monto);
    const totalBottom = y - ROW_H;
    page.drawRectangle({
      x: ML,
      y: totalBottom,
      width: CW,
      height: ROW_H,
      color: C.textoOscuro,
    });
    page.drawText("TOTAL", {
      x: COL_FOLIO,
      y: totalBottom + TEXT_PAD,
      size: 10,
      font: fB,
      color: C.blanco,
    });
    page.drawText(totalStr, {
      x: COL_MONTO_R - fB.widthOfTextAtSize(totalStr, 10),
      y: totalBottom + TEXT_PAD,
      size: 10,
      font: fB,
      color: C.blanco,
    });
    y = totalBottom - 16;

    // ---- Detalles de transacción ----
    filasTransaccion.forEach(
      ({ label, value, esIdentidad, lineasValor, rowHeight }, idx) => {
        const rowBottom = y - rowHeight;
        if (idx % 2 === 0) {
          page.drawRectangle({
            x: ML,
            y: rowBottom,
            width: CW,
            height: rowHeight,
            color: C.bgTabla,
          });
        }
        const textY = y - ROW_H + TEXT_PAD;
        page.drawText(label, {
          x: ML + 12,
          y: textY,
          size: 10,
          font: fB,
          color: C.textoGris,
        });
        if (esIdentidad) {
          lineasValor.forEach((linea, lineaIndex) => {
            page.drawText(linea, {
              x: ML + 12,
              y: textY - 13 - lineaIndex * 12,
              size: 10,
              font: fR,
              color: C.textoOscuro,
            });
          });
        } else {
          page.drawText(value, {
            x: COL2,
            y: textY,
            size: 10,
            font: fR,
            color: C.textoOscuro,
          });
        }
        page.drawLine({
          start: { x: ML, y: rowBottom },
          end: { x: MR, y: rowBottom },
          thickness: 0.5,
          color: C.lineaSep,
        });
        y = rowBottom;
      },
    );
  } else {
    // ---- Modo individual: tabla original sin cambios ----
    const descripcionServicio =
      descripcion ??
      (tipoPago === "PLAN"
        ? "Pago de factura — Plan mensual"
        : tipoPago === "TAREA"
          ? "Pago de factura — Tarea adicional"
          : "Pago de factura — Servicio contable");

    const filas = [
      ["Comercio", "Taxtic"],
      ["Descripción", descripcionServicio],
      ...(formatearIdentidad({ nombre: nombreSociedad, rut: rutSociedad })
        ? [
            [
              "Sociedad titular de la factura",
              formatearIdentidad({
                nombre: nombreSociedad,
                rut: rutSociedad,
              }),
              true,
            ],
          ]
        : []),
      ...(formatearIdentidad(cuentaPagadora)
        ? [
            [
              "Cuenta que realizó el pago",
              formatearIdentidad(cuentaPagadora),
              true,
            ],
          ]
        : []),
      ["Folio Factura", folioStr],
      ["Fecha de Pago", formatearFecha(transaction_date)],
      ["Tipo de Tarjeta", tipoTarjeta],
      ["Últimos 4 dígitos", `**** ${ultimos4}`],
      ["Cuotas", cuotas],
    ];

    filas.forEach(([label, value, usarAnchoCompleto = false], idx) => {
      const lineasValor = usarAnchoCompleto
        ? dividirTextoPorAncho({
            texto: value,
            font: fR,
            size: 10,
            maxWidth: CW - 24,
          })
        : [];
      const rowHeight = ROW_H + lineasValor.length * 12;
      const rowBottom = y - rowHeight;
      if (idx % 2 === 1) {
        page.drawRectangle({
          x: ML,
          y: rowBottom,
          width: CW,
          height: rowHeight,
          color: C.bgTabla,
        });
      }
      const textY = y - ROW_H + TEXT_PAD;
      page.drawText(label, {
        x: ML + 12,
        y: textY,
        size: 10,
        font: fB,
        color: C.textoGris,
      });
      if (usarAnchoCompleto) {
        lineasValor.forEach((linea, lineaIndex) => {
          page.drawText(linea, {
            x: ML + 12,
            y: textY - 13 - lineaIndex * 12,
            size: 10,
            font: fR,
            color: C.textoOscuro,
          });
        });
      } else {
        page.drawText(value, {
          x: COL2,
          y: textY,
          size: 10,
          font: fR,
          color: C.textoOscuro,
        });
      }
      page.drawLine({
        start: { x: ML, y: rowBottom },
        end: { x: MR, y: rowBottom },
        thickness: 0.5,
        color: C.lineaSep,
      });
      y = rowBottom;
    });
  }

  y -= 24;

  // ── Caja código de autorización ───────────────────────────────────────────
  const AUTH_H = 76;
  const authBottom = y - AUTH_H;

  page.drawRectangle({
    x: ML,
    y: authBottom,
    width: CW,
    height: AUTH_H,
    color: C.bgAuth,
    borderColor: C.bordeAuth,
    borderWidth: 1,
  });

  const labelAuth = "CÓDIGO DE AUTORIZACIÓN";
  page.drawText(labelAuth, {
    x: ML + CW / 2 - fB.widthOfTextAtSize(labelAuth, 8) / 2,
    y: authBottom + AUTH_H - 18,
    size: 8,
    font: fB,
    color: C.textoGris,
  });
  page.drawText(authorization_code, {
    x: ML + CW / 2 - fB.widthOfTextAtSize(authorization_code, 26) / 2,
    y: authBottom + AUTH_H / 2 - 26 * 0.35,
    size: 26,
    font: fB,
    color: C.textoOscuro,
  });

  const notaAuth = "Conserva este código como respaldo de tu transacción.";
  page.drawText(notaAuth, {
    x: ML + CW / 2 - fR.widthOfTextAtSize(notaAuth, 8) / 2,
    y: authBottom + 10,
    size: 8,
    font: fR,
    color: C.textoClaro,
  });

  y = authBottom - 20;

  // ── Nota de contacto ──────────────────────────────────────────────────────
  page.drawText(
    "Si tienes consultas sobre este pago, contáctanos en clientes@taxtic.com indicando el código de autorización.",
    {
      x: ML,
      y,
      size: 9,
      font: fR,
      color: C.textoGris,
      maxWidth: CW,
      lineHeight: 14,
    },
  );

  const paginas = doc.getPages();
  paginas.forEach((pagina, index) =>
    dibujarFooter(pagina, index + 1, paginas.length),
  );

  // ── Descargar en el browser ───────────────────────────────────────────────
  const pdfBytes = await doc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = esMultiple
    ? `Comprobante_Pago_Multiple_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.pdf`
    : `Comprobante_${folioStr.replace(/\//g, "-")}.pdf`;
  a.click();

  // Liberar la URL del objeto después de un breve delay
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
